<template>
  <div>
    <section class="relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24 bg-white dark:bg-dark transition-colors duration-300">
      <div class="absolute -top-24 -left-24 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div class="absolute -bottom-24 -right-24 w-72 h-72 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-4xl mx-auto text-center">
          <div class="inline-flex items-center gap-2 rounded-full bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.08] px-4 py-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400 mb-7">
            <span class="w-2 h-2 rounded-full bg-blue-500"></span>
            {{ tr('Chi siamo', 'About us') }}
          </div>

          <h1 class="text-[clamp(2.1rem,4.2vw,4.2rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-gray-900 dark:text-white mb-6">
            {{ tr('Due studenti universitari che costruiscono', 'Two university students building') }}
            <span class="hero-gradient-text"> {{ tr('soluzioni digitali fatte per funzionare davvero', 'digital solutions that actually work') }}</span>
          </h1>

          <p class="text-lg md:text-xl text-gray-500 dark:text-gray-400 leading-relaxed max-w-3xl mx-auto mb-9">
            {{ tr('Siamo il team dietro Ciobai. Uniamo sviluppo web, automazione AI e attenzione ai dettagli per aiutare aziende e professionisti a lavorare meglio, ridurre sprechi operativi e crescere con una base digitale solida.', 'We are the team behind Ciobai. We combine web development, AI automation, and attention to detail to help companies and professionals work better, reduce operational waste, and grow on a solid digital foundation.') }}
          </p>

          <div class="flex flex-col sm:flex-row gap-3 justify-center">
            <router-link
              to="/contact"
              class="inline-flex items-center justify-center h-12 px-7 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              {{ tr('Parliamone', "Let's talk") }}
            </router-link>
            <router-link
              to="/automazioni-ai"
              class="inline-flex items-center justify-center h-12 px-7 border border-gray-200 dark:border-white/[0.1] text-gray-600 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
            >
              {{ tr('Vedi cosa possiamo automatizzare', 'See what we can automate') }}
            </router-link>
          </div>
        </div>

        <div class="max-w-6xl mx-auto mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          <article class="rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] p-6 text-left">
            <p class="text-xs uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500 mb-3">{{ tr('Identita', 'Identity') }}</p>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">{{ tr('Team snello, alta attenzione', 'Small team, high attention') }}</h3>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              {{ tr('Siamo in due, quindi ogni progetto e seguito direttamente da chi lo progetta e lo sviluppa.', 'We are a two-person team, so each project is handled directly by the people designing and building it.') }}
            </p>
          </article>
          <article class="rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] p-6 text-left">
            <p class="text-xs uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500 mb-3">{{ tr('Focus', 'Focus') }}</p>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">{{ tr('Web e AI insieme', 'Web and AI together') }}</h3>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              {{ tr('Progettiamo siti performanti e automazioni intelligenti come un unico sistema, non come blocchi separati.', 'We design high-performing websites and smart automations as one system, not separate blocks.') }}
            </p>
          </article>
          <article class="rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] p-6 text-left">
            <p class="text-xs uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500 mb-3">{{ tr('Approccio', 'Approach') }}</p>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">{{ tr('Concretezza prima di tutto', 'Execution first') }}</h3>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              {{ tr('Meno slide e piu esecuzione: testiamo, misuriamo e miglioriamo in iterazioni rapide.', 'Less slides, more execution: we test, measure, and improve in fast iterations.') }}
            </p>
          </article>
        </div>
      </div>
    </section>

    <section class="py-20 bg-white dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="max-w-5xl mx-auto text-center mb-10">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ tr('Il nostro metodo', 'Our method') }}</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">{{ tr('Dal problema ai risultati, senza complicazioni', 'From problem to results, without the noise') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400">
            {{ tr('Non proponiamo pacchetti standard. Definiamo priorita reali, rilasciamo step utili e miglioriamo con dati concreti.', 'We do not sell generic packages. We define real priorities, ship useful milestones, and improve with real data.') }}
          </p>
        </div>

        <div class="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          <article class="rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] p-6">
            <p class="text-xs font-semibold text-blue-500 mb-2">{{ tr('01 - Analisi', '01 - Analysis') }}</p>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">{{ tr('Capiamo il contesto', 'We understand the context') }}</h3>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              {{ tr('Entriamo nel tuo flusso operativo e individuiamo subito dove puoi ottenere il massimo impatto.', 'We step into your workflow and identify where you can get the biggest impact quickly.') }}
            </p>
          </article>
          <article class="rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] p-6">
            <p class="text-xs font-semibold text-blue-500 mb-2">{{ tr('02 - Progettazione', '02 - Design') }}</p>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">{{ tr('Definiamo una soluzione semplice', 'We define a simple solution') }}</h3>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              {{ tr('Strutturiamo una roadmap chiara: obiettivi, tempi, responsabilita e deliverable.', 'We structure a clear roadmap: goals, timing, ownership, and deliverables.') }}
            </p>
          </article>
          <article class="rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.03] p-6">
            <p class="text-xs font-semibold text-blue-500 mb-2">{{ tr('03 - Iterazione', '03 - Iteration') }}</p>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">{{ tr('Miglioriamo con i dati', 'We improve with data') }}</h3>
            <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
              {{ tr('Rilasciamo rapidamente, monitoriamo performance e ottimizziamo in modo continuo.', 'We ship fast, monitor performance, and optimize continuously.') }}
            </p>
          </article>
        </div>
      </div>
    </section>

    <section class="py-20 bg-gray-50 dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="max-w-5xl mx-auto rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] p-8 md:p-10">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h2 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">{{ tr('Perche ci scelgono', 'Why clients choose us') }}</h2>
              <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                {{ tr("Perche lavoriamo in modo trasparente e orientato all'esecuzione. Ogni progetto deve essere bello da vedere, semplice da usare e utile per il business.", 'Because we work transparently and execution-first. Every project should look great, be easy to use, and create business value.') }}
              </p>
            </div>
            <div class="space-y-3">
              <div class="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.02] p-4">
                <p class="text-sm font-semibold text-gray-900 dark:text-white mb-1">{{ tr('Velocita di esecuzione', 'Execution speed') }}</p>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ tr('Tempi brevi, feedback continuo, rilascio per fasi.', 'Short cycles, continuous feedback, phased delivery.') }}</p>
              </div>
              <div class="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.02] p-4">
                <p class="text-sm font-semibold text-gray-900 dark:text-white mb-1">{{ tr('Qualita tecnica', 'Technical quality') }}</p>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ tr('Design pulito, performance solide, attenzione al dettaglio.', 'Clean design, strong performance, attention to detail.') }}</p>
              </div>
              <div class="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-gray-50 dark:bg-white/[0.02] p-4">
                <p class="text-sm font-semibold text-gray-900 dark:text-white mb-1">{{ tr('Relazione trasparente', 'Transparent collaboration') }}</p>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ tr('Comunicazione diretta, scelte condivise, niente promesse vuote.', 'Direct communication, shared decisions, no empty promises.') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="py-20 bg-white dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">{{ tr('Se vuoi, partiamo con una call da 20 minuti', 'If you want, we can start with a 20-minute call') }}</h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 mb-8">
            {{ tr('Ci racconti il tuo obiettivo e ti proponiamo una direzione concreta su cui iniziare subito.', 'You share your goal, and we propose a concrete direction you can start with right away.') }}
          </p>
          <router-link
            to="/contact"
            class="inline-flex items-center h-12 px-7 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
          >
            {{ tr('Prenota la call', 'Book the call') }}
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()
const isEn = computed(() => (locale.value || '').startsWith('en'))
const tr = (it, en) => (isEn.value ? en : it)

defineOptions({
  name: 'ChiSiamoPage'
})
</script>

<style scoped>
.hero-gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
</style>
